<template>
  <base-layout :do-skip-header="true">
    <template #main>
      <h1 class="text-3xl md:text-4xl font-bold mt-8">European Broker Meeting Waiting List</h1>

      <div
        v-if="registrationSuccess"
        class="flex items-center justify-center mt-6"
      >
        <div
          class="w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center"
        >
          <!-- Success Icon -->
          <div class="flex justify-center mb-6">
            <div
              class="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-10 h-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          <!-- Title -->
          <h2 class="text-2xl font-bold text-gray-900">
            Registration Successful!
          </h2>

          <!-- Message -->
          <p class="mt-3 text-gray-600 leading-relaxed">
            Thank you for registering your company. Your information has been
            successfully submitted to our waiting list.
          </p>
        </div>
      </div>

      <ValidationObserver v-else ref="observer" v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(submit)" class=" mx-auto">
          <div class="bg-white rounded-2xl shadow-sm border-gray-100 p-6 md:p-8 mt-4">
            <!-- Header -->
            <div class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900">Company Information</h2>
              <p class="mt-1 text-sm text-gray-500">Enter the company and contact details below. We will contact you as soon as more spots become available.</p>
            </div>

            <div class="space-y-6">
              <!-- Company Name -->
              <ValidationProvider name="Company name" rules="required" v-slot="{ errors }">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Company name
                    <span class="text-red-500">*</span>
                  </label>

                  <input v-model="form.companyName" type="text" :class="['w-full px-4 py-3 rounded-xl border text-sm', 'outline-none transition duration-200', 'placeholder-gray-400', errors.length ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:border-farm-500 focus:ring-2 focus:ring-farm-100']" />

                  <p v-if="errors.length" class="mt-1.5 text-xs text-red-500">
                    {{ errors[0] }}
                  </p>
                </div>
              </ValidationProvider>

              <!-- Contact Person -->
              <ValidationProvider name="Contact person" rules="required" v-slot="{ errors }">
                <div class=" mt-6">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Contact person
                    <span class="text-red-500">*</span>
                  </label>

                  <input v-model="form.contactPerson" type="text" :class="['w-full px-4 py-3 rounded-xl border text-sm', 'outline-none transition duration-200', 'placeholder-gray-400', errors.length ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:border-farm-500 focus:ring-2 focus:ring-farm-100']" />

                  <p v-if="errors.length" class="mt-1.5 text-xs text-red-500">
                    {{ errors[0] }}
                  </p>
                </div>
              </ValidationProvider>

              <!-- Email -->
              <ValidationProvider name="Email address" rules="required|email" v-slot="{ errors }">
                <div class=" mt-6">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Email address
                    <span class="text-red-500">*</span>
                  </label>

                  <input v-model="form.email" type="email" :class="['w-full px-4 py-3 rounded-xl border text-sm', 'outline-none transition duration-200', 'placeholder-gray-400', errors.length ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:border-farm-500 focus:ring-2 focus:ring-farm-100']" />

                  <p v-if="errors.length" class="mt-1.5 text-xs text-red-500">
                    {{ errors[0] }}
                  </p>
                </div>
              </ValidationProvider>

              <!-- Mobile Number -->
              <ValidationProvider name="Mobile number" rules="required" v-slot="{ errors }">
                <div class=" mt-6">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Mobile number
                    <span class="text-red-500">*</span>
                  </label>

                  <input v-model="form.mobileNumber" type="tel" :class="['w-full px-4 py-3 rounded-xl border text-sm', 'outline-none transition duration-200', 'placeholder-gray-400', errors.length ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:border-farm-500 focus:ring-2 focus:ring-farm-100']" />

                  <p v-if="errors.length" class="mt-1.5 text-xs text-red-500">
                    {{ errors[0] }}
                  </p>
                </div>
              </ValidationProvider>
            </div>

            <!-- Submit -->
            <div class="flex justify-end mt-8">
              <button type="submit" :disabled="loading"
              class="rounded-md border border-transparent bg-red-600 py-2 px-4 font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60">Submit</button>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </template>
  </base-layout>
</template>

<script>
import _ from "lodash"
import BaseLayout from "../components/BaseLayout.vue"
import { ValidationProvider, ValidationObserver, extend } from "vee-validate"
import { mapActions } from "vuex";


import { required, email } from "vee-validate/dist/rules"

extend("required", {
  ...required,
  message: "{_field_} is required",
})

extend("email", {
  ...email,
  message: "Please enter a valid email address",
})

export default {
  name: "WaitingList",
  components: {
    BaseLayout,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      form: {
        companyName: "",
        contactPerson: "",
        email: "",
        mobileNumber: "",
        loading: false,
      },
      registrationSuccess: false
    }
  },

  methods: {
    ...mapActions(["registerToWaitingList"]),
    async submit() {
      try {
        this.loading = true;
        await this.registerToWaitingList(this.form)
        this.registrationSuccess = true;
        this.loading = false;
        this.resetForm();
      } catch (error) {
        console.error(error)
      }
    },

    resetForm() {
      this.form = {
        companyName: "",
        contactPerson: "",
        email: "",
        mobileNumber: "",
      }

      this.$refs.observer.reset()
    },
  },
}
</script>
<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
